import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { message, notification } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import scheduleApi from 'shared/api/schedule.service.api';
import { useCreateEventGroup, useUpdateEventGroup } from 'shared/hooks/schedule-events.hooks';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getUser } from 'shared/store/selectors/user.selector';
import { formatFullName, formatNumber } from 'shared/utils';
import {
  buildEducationalEventGroupBody,
  buildEducationalEventGroupPatch,
} from 'shared/utils/educational-event-group';
import { countRecurrenceOccurrences } from 'shared/utils/recurrence';
import {
  buildCheckInWindowPatch,
  getCheckInOffsetOptions,
  getCheckInWindowFromEvent,
  hasEventStarted,
  toCheckInWindowMinutes,
} from 'shared/utils/check-in-window';
import {
  ATTENDANCE_MODES,
  CADENCES,
  CHECK_IN_WINDOW_SIDES,
  DEFAULT_CHECK_IN_WINDOW,
  QR_TYPES,
  RECURRENCE_NONE,
  RECURRING_EDIT_SCOPE,
  attendanceModeUsesWebLink,
  effectiveWebLink,
} from 'shared/constants/schedule.constants';
import { USER_PERMISSIONS } from 'shared/constants/auth.constants';
import { KNOWLEDGE_RESOURCES_TYPES, EVENTS, SCHEDULE_EVENT_TYPES } from 'shared/constants';
import {
  Modal,
  Button,
  TextInput,
  SelectBox,
  DatePicker,
  Divider,
  RequiredIndication,
} from 'src/components/design-system';
import { Tooltip } from 'src/components/core/tooltip/tooltip.component';
import ResourceManager from 'src/components/core/select-content/resource-manager.component';
import { RecurringScopeModal } from '../recurring-scope-modal/recurring-scope-modal';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { useEducationalEventsContext, FORM_MODE } from '../../educational-events-context';
import { useAttendanceModeLabels } from 'shared/hooks/schedule.hooks';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import { getHostIds, isCheckInSessionOpen, isPastEvent } from '../../helpers/event-view.helpers';
import { eventsKeys } from '../../helpers/query-keys';
import styles from './event-form-modal.module.scss';

const DATE_TIME_FORMAT = 'LLL d, yyyy h:mm a';

const RESOURCE_ROW_OPTIONS = {
  showContentType: false,
  showTags: false,
  showLabels: false,
  showMetadata: false,
};

/**
 * Internal SelectBox values for the check-in method field — SelectBox can't
 * bind a raw boolean (lodash `isEmpty` treats every boolean as empty), so the
 * select works in these string tokens and maps to/from the boolean Formik field.
 */
const CHECK_IN_METHOD_VALUES = {
  QR: 'qr',
  SELF_ATTESTATION: 'self-attestation',
};

function LockTooltip({ locked, hint, className, children }) {
  const cell = <div className={classNames(styles.lockTooltipTarget, className)}>{children}</div>;
  if (!locked || !hint) return cell;
  return (
    <Tooltip title={hint} placement="top">
      {cell}
    </Tooltip>
  );
}

function sameTime(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return new Date(a).getTime() === new Date(b).getTime();
}

function sameIds(a = [], b = []) {
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort();
  return [...a].sort().every((id, index) => id === sortedB[index]);
}

function resourceKey(item) {
  return item?.type === KNOWLEDGE_RESOURCES_TYPES.EXTERNAL_URL ? item?.url : item?.id;
}

function buildAddEventSubmitProps(values) {
  return {
    type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
    eventTitle: values.title.trim(),
    hostCount: values.hostIds.length,
    cadence: values.cadence,
  };
}

function buildPatch(event, values) {
  const patch = {};
  const attributes = {};

  if (values.title.trim() !== (event.title || '')) patch.title = values.title.trim();
  if ((values.location || '') !== (event.location || '')) patch.location = values.location || '';
  if (!sameTime(values.startTime, event.startTime)) patch.startTime = values.startTime;
  if (!sameTime(values.endTime, event.endTime)) patch.endTime = values.endTime;

  if (!sameIds(values.hostIds, getHostIds(event))) attributes.hostIds = values.hostIds;
  if ((values.speaker || '') !== (event.attributes?.speaker || '')) {
    attributes.speaker = values.speaker || '';
  }
  if (values.format !== event.attributes?.attendanceMode) attributes.attendanceMode = values.format;

  const webLink = effectiveWebLink(values.format, values.webLink);
  if (webLink !== (event.attributes?.webLink || '')) attributes.webLink = webLink;

  if (values.allowSelfAttestation !== Boolean(event.attributes?.allowSelfAttestation)) {
    attributes.allowSelfAttestation = values.allowSelfAttestation;
  }

  if (values.qrType !== (event.attributes?.qrType || QR_TYPES.ROTATING)) {
    attributes.qrType = values.qrType;
  }

  Object.assign(attributes, buildCheckInWindowPatch({ event, values }));

  if (Object.keys(attributes).length) patch.attributes = attributes;
  return patch;
}

// Series' first date ('YYYY-MM-DD', org tz): the start-time's date on create,
// the group's stored series start on edit (which isn't editable).
function getSeriesStartDate({ isEdit, formEvent, startTime, orgTimezone }) {
  if (isEdit) return formEvent?.group?.recurrence?.startDate || '';
  if (!startTime) return '';
  return DateTime.fromISO(startTime, orgTimezone ? { zone: orgTimezone } : {}).toFormat(
    'yyyy-LL-dd',
  );
}

// Cadence/end-date change is series-level (no "only this" meaning) → skips the
// scope prompt. A start-date change is per-occurrence-ambiguous, so it doesn't.
function didRecurrenceChange(formEvent, values) {
  const recurrence = formEvent?.group?.recurrence;
  if (!recurrence) return false;
  return values.cadence !== recurrence.cadence || values.recurrenceEndDate !== recurrence.endDate;
}

// The changed recurrence fields for a group edit, or null if unchanged. A
// start-date move re-anchors the series to the edited occurrence's new date.
function buildRecurrencePatch(formEvent, values, orgTimezone) {
  const current = formEvent?.group?.recurrence;
  if (!current) return null;
  const recurrence = {};
  if (values.cadence !== current.cadence) recurrence.cadence = values.cadence;
  if (values.recurrenceEndDate !== current.endDate) recurrence.endDate = values.recurrenceEndDate;
  const zone = orgTimezone ? { zone: orgTimezone } : {};
  const newStart = DateTime.fromISO(values.startTime, zone).toFormat('yyyy-LL-dd');
  const oldStart = DateTime.fromISO(formEvent.startTime, zone).toFormat('yyyy-LL-dd');
  if (newStart !== oldStart) recurrence.startDate = newStart;
  return Object.keys(recurrence).length ? recurrence : null;
}

export function EventFormModal() {
  const { lang, userId, users, formMode, formEvent, closeForm, setSelectedEventId } =
    useEducationalEventsContext();
  const createLang = useSelector(getLang('CREATE_EDUCATIONAL_EVENT'));
  const contentSelectorLang = useSelector(getLang('CONTENT_SELECTOR'));
  const currentUser = useSelector(getUser);
  const queryClient = useQueryClient();
  const [resourceApi, resourceContextHolder] = notification.useNotification();
  const { formatOptions } = useAttendanceModeLabels();
  const { orgTimezone, orgTimezoneAbbr } = useEducationalEventFormat();

  const isEdit = formMode === FORM_MODE.EDIT;

  const checkInLocked = isEdit && isCheckInSessionOpen(formEvent);
  const finishedLocked = isEdit && isPastEvent(formEvent);
  const lockTimeAndHost = checkInLocked || finishedLocked;
  const getLockHint = () => {
    if (checkInLocked) return createLang.FIELDS_LOCKED_HINT_CHECK_IN;
    if (finishedLocked) return createLang.FIELDS_LOCKED_HINT_FINISHED;
    return null;
  };
  const lockHint = getLockHint();
  const checkInOpensLocked = isEdit && hasEventStarted({ event: formEvent });

  const hostOptions = useMemo(() => {
    const labelFor = user =>
      formatFullName(user, { labelInParenthesis: user.id === userId ? createLang.YOU : null });
    const options = users
      .filter(user => user.permissions !== USER_PERMISSIONS.VIEWER)
      .map(user => ({ value: user.id, label: labelFor(user) }));
    if (userId && !options.some(option => option.value === userId)) {
      options.unshift({ value: userId, label: labelFor({ ...currentUser, id: userId }) });
    }
    return options;
  }, [users, userId, currentUser, createLang.YOU]);

  const checkInMethodOptions = [
    { value: CHECK_IN_METHOD_VALUES.QR, label: lang.CHECK_IN_METHOD_OPTION_QR },
    {
      value: CHECK_IN_METHOD_VALUES.SELF_ATTESTATION,
      label: lang.CHECK_IN_METHOD_OPTION_SELF_ATTESTATION,
    },
  ];

  const qrTypeOptions = [
    { value: QR_TYPES.ROTATING, label: lang.QR_TYPE_OPTION_ROTATING },
    { value: QR_TYPES.STATIC, label: lang.QR_TYPE_OPTION_STATIC },
  ];

  const initialValues = useMemo(() => {
    if (isEdit && formEvent) {
      const recurrence = formEvent.group?.recurrence;
      const checkInWindow = getCheckInWindowFromEvent(formEvent);
      return {
        title: formEvent.title || '',
        location: formEvent.location || '',
        hostIds: getHostIds(formEvent),
        speaker: formEvent.attributes?.speaker || '',
        startTime: formEvent.startTime || '',
        endTime: formEvent.endTime || '',
        format: formEvent.attributes?.attendanceMode || ATTENDANCE_MODES.IN_PERSON,
        webLink: formEvent.attributes?.webLink || '',
        allowSelfAttestation: Boolean(formEvent.attributes?.allowSelfAttestation),
        qrType: formEvent.attributes?.qrType || QR_TYPES.ROTATING,
        checkInOpensBeforeStartMinutes: String(checkInWindow.checkInOpensBeforeStartMinutes),
        checkInClosesAfterEndMinutes: String(checkInWindow.checkInClosesAfterEndMinutes),
        knowledgeItems: [],
        cadence: recurrence?.cadence || RECURRENCE_NONE,
        recurrenceEndDate: recurrence?.endDate || '',
      };
    }
    const start = DateTime.now().plus({ hours: 1 }).startOf('hour');
    return {
      title: '',
      location: '',
      hostIds: userId ? [userId] : [],
      speaker: '',
      startTime: start.toISO(),
      endTime: start.plus({ hours: 1 }).toISO(),
      format: ATTENDANCE_MODES.IN_PERSON,
      webLink: '',
      allowSelfAttestation: false,
      qrType: QR_TYPES.ROTATING,
      checkInOpensBeforeStartMinutes: String(
        DEFAULT_CHECK_IN_WINDOW.checkInOpensBeforeStartMinutes,
      ),
      checkInClosesAfterEndMinutes: String(DEFAULT_CHECK_IN_WINDOW.checkInClosesAfterEndMinutes),
      knowledgeItems: [],
      cadence: RECURRENCE_NONE,
      recurrenceEndDate: '',
    };
  }, [isEdit, formEvent, userId]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        title: Yup.string().trim().required(createLang.VALIDATION_TITLE_REQUIRED),
        hostIds: Yup.array().min(1, createLang.VALIDATION_HOST_REQUIRED),
        startTime: Yup.string().required(),
        endTime: Yup.string()
          .required()
          .test('after-start', createLang.VALIDATION_END_BEFORE_START, function testEnd(end) {
            const { startTime } = this.parent;
            if (!startTime || !end) return true;
            return new Date(end).getTime() > new Date(startTime).getTime();
          }),
        recurrenceEndDate: Yup.string().test(
          'recurrence-end',
          '',
          function testRecurrenceEnd(endDate) {
            const { cadence, startTime } = this.parent;
            if (cadence === RECURRENCE_NONE) return true;
            if (!endDate) return false;
            const startDate = getSeriesStartDate({ isEdit, formEvent, startTime, orgTimezone });
            if (!startDate) return false;
            // Series can't run past start + 2 years (backend limit).
            const maxDate = DateTime.fromISO(startDate).plus({ years: 2 }).toFormat('yyyy-LL-dd');
            if (endDate > maxDate) return false;
            return countRecurrenceOccurrences({ cadence, startDate, endDate }) >= 1;
          },
        ),
      }),
    [createLang, orgTimezone, isEdit, formEvent],
  );

  const patchMutation = useMutation({
    mutationFn: patch => scheduleApi.patchEvent({ eventId: formEvent.id, patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.list() });
      queryClient.invalidateQueries({ queryKey: eventsKeys.detail(formEvent.id) });
    },
    onError: error => {
      monitorService.error('Failed to save educational event', { error });
      message.error(createLang.SAVE_FAILED);
    },
  });

  const createMutation = useMutation({
    mutationFn: async attributes => {
      const data = await scheduleApi.createEducationalEvent(attributes);
      return data?.scheduleEvent || data;
    },
    onError: error => {
      monitorService.error('Failed to create educational event', { error });
      message.error(createLang.SAVE_FAILED);
    },
  });

  const createGroup = useCreateEventGroup();
  const updateGroup = useUpdateEventGroup();

  // Form values stashed while the scope prompt is open; null when closed.
  const [scopeValues, setScopeValues] = useState(null);
  const isEditingSeries = isEdit && !!formEvent?.scheduleEventGroupId;

  const cadenceOptions = useMemo(() => {
    const cadences = [
      { value: CADENCES.DAILY, label: createLang.REPEAT_DAILY },
      { value: CADENCES.WEEKLY, label: createLang.REPEAT_WEEKLY },
      { value: CADENCES.BI_WEEKLY, label: createLang.REPEAT_BI_WEEKLY },
      { value: CADENCES.MONTHLY, label: createLang.REPEAT_MONTHLY },
      { value: CADENCES.QUARTERLY, label: createLang.REPEAT_QUARTERLY },
    ];
    // "Does not repeat" is create-only — a recurring event stays recurring.
    return isEdit
      ? cadences
      : [{ value: RECURRENCE_NONE, label: createLang.REPEAT_NONE }, ...cadences];
  }, [createLang, isEdit]);

  // "Only this event" (and non-recurring edits): PATCH the single occurrence.
  const saveSingleEdit = async patch => {
    try {
      await patchMutation.mutateAsync(patch);
    } catch {
      return;
    }
    const recurring = !!formEvent.scheduleEventGroupId;
    analyticsService.track(EVENTS.SCHEDULE.EDIT_EVENT_SUBMIT, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      eventId: formEvent.id,
      recurring,
      ...(recurring && {
        scope: RECURRING_EDIT_SCOPE.SINGLE,
        scheduleEventGroupId: formEvent.scheduleEventGroupId,
      }),
    });
    setScopeValues(null);
    closeForm();
  };

  // "This event and all future occurrences": PATCH the group with only the
  // changed fields + changed recurrence (a start-date move re-anchors the series).
  const saveGroupEdit = async values => {
    const patch = buildEducationalEventGroupPatch(buildPatch(formEvent, values), orgTimezone);
    const recurrence = buildRecurrencePatch(formEvent, values, orgTimezone);
    if (recurrence) {
      patch.details = { ...(patch.details || {}), recurrence };
    }
    // recurrence.startDate is only set when the start day moved — the one case
    // the backend rejects for past dates.
    if (recurrence?.startDate) {
      const today = (orgTimezone ? DateTime.now().setZone(orgTimezone) : DateTime.now()).toFormat(
        'yyyy-LL-dd',
      );
      if (recurrence.startDate < today) {
        message.error(createLang.VALIDATION_START_IN_PAST);
        return;
      }
    }
    try {
      await updateGroup.mutateAsync({ groupId: formEvent.scheduleEventGroupId, patch });
    } catch (error) {
      monitorService.error('Failed to update recurring event group', { error });
      message.error(createLang.SAVE_FAILED);
      return;
    }
    analyticsService.track(EVENTS.SCHEDULE.EDIT_EVENT_SUBMIT, {
      type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
      eventId: formEvent.id,
      recurring: true,
      scope: RECURRING_EDIT_SCOPE.GROUP,
      scheduleEventGroupId: formEvent.scheduleEventGroupId,
    });
    setScopeValues(null);
    closeForm();
  };

  const onSubmit = async values => {
    if (isEdit) {
      const patch = buildPatch(formEvent, values);
      const fieldsChanged = Object.keys(patch).length > 0;
      const recurrenceChanged = didRecurrenceChange(formEvent, values);
      if (!fieldsChanged && !recurrenceChanged) {
        closeForm();
        return;
      }
      if (isEditingSeries) {
        // A cadence/end-date change is series-level: skip the prompt, edit the group.
        if (recurrenceChanged) {
          await saveGroupEdit(values);
          return;
        }
        // Field-only edit: let the user choose this occurrence vs the series.
        setScopeValues(values);
        return;
      }
      await saveSingleEdit(patch);
      return;
    }

    if (values.cadence !== RECURRENCE_NONE) {
      try {
        await createGroup.mutateAsync(
          buildEducationalEventGroupBody({ ...values, attendanceMode: values.format }, orgTimezone),
        );
      } catch (error) {
        monitorService.error('Failed to create recurring event group', { error });
        message.error(createLang.SAVE_FAILED);
        return;
      }
      analyticsService.track(EVENTS.SCHEDULE.ADD_EVENT_SUBMIT, buildAddEventSubmitProps(values));
      closeForm();
      return;
    }

    let createdEvent;
    try {
      createdEvent = await createMutation.mutateAsync({
        title: values.title.trim(),
        location: values.location || '',
        startTime: values.startTime,
        endTime: values.endTime,
        isAllDay: false,
        attributes: {
          hostIds: values.hostIds,
          speaker: values.speaker || '',
          knowledgeItems: values.knowledgeItems || [],
          attendanceMode: values.format,
          webLink: effectiveWebLink(values.format, values.webLink),
          allowSelfAttestation: values.allowSelfAttestation,
          qrType: values.qrType,
          ...toCheckInWindowMinutes(values),
          notifyAttendees: false,
        },
      });
    } catch {
      return;
    }
    analyticsService.track(EVENTS.SCHEDULE.ADD_EVENT_SUBMIT, buildAddEventSubmitProps(values));
    closeForm();
    await queryClient.invalidateQueries({ queryKey: eventsKeys.list() });
    if (createdEvent?.id) setSelectedEventId(createdEvent.id);
  };

  const handleScopeOnlyThis = () => saveSingleEdit(buildPatch(formEvent, scopeValues));
  const handleScopeAllFollowing = () => saveGroupEdit(scopeValues);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues,
    validationSchema,
    onSubmit,
  });

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = !!formMode;
    if (isOpen && !wasOpenRef.current) {
      formik.resetForm({ values: initialValues });
      formik.validateForm(initialValues);
    }
    wasOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode, initialValues]);

  const timeLabel = (label, time) => {
    const tz = orgTimezoneAbbr(time);
    return tz ? `${label} (${tz})` : label;
  };

  const hasErrors = Object.keys(formik.errors).length > 0;

  const storedCheckInWindow = isEdit ? getCheckInWindowFromEvent(formEvent) : null;
  const checkInOpensOptions = getCheckInOffsetOptions({
    lang,
    side: CHECK_IN_WINDOW_SIDES.OPEN,
    currentMinutes: storedCheckInWindow?.checkInOpensBeforeStartMinutes,
  });
  const checkInClosesOptions = getCheckInOffsetOptions({
    lang,
    side: CHECK_IN_WINDOW_SIDES.CLOSE,
    currentMinutes: formik.values.checkInClosesAfterEndMinutes
      ? Number(formik.values.checkInClosesAfterEndMinutes)
      : storedCheckInWindow?.checkInClosesAfterEndMinutes,
    event: isEdit ? { endTime: formik.values.endTime || formEvent?.endTime } : null,
  });

  const endConstraints = useMemo(() => {
    if (!formik.values.startTime) return { minDate: undefined, disabledTime: undefined };
    const start = DateTime.fromISO(
      formik.values.startTime,
      orgTimezone ? { zone: orgTimezone } : {},
    );
    const disabledTime = current => {
      if (!current || !current.hasSame(start, 'day')) return {};
      return {
        disabledHours: () => Array.from({ length: start.hour }, (_, hour) => hour),
        disabledMinutes: selectedHour =>
          selectedHour === start.hour
            ? Array.from({ length: start.minute + 1 }, (_, minute) => minute)
            : [],
      };
    };
    return { minDate: start.startOf('day'), disabledTime };
  }, [formik.values.startTime, orgTimezone]);

  const isRecurring = formik.values.cadence !== RECURRENCE_NONE;
  const showRepeatFields = !isEdit || isRecurring;

  const recurrenceStartDate = useMemo(
    () =>
      getSeriesStartDate({ isEdit, formEvent, startTime: formik.values.startTime, orgTimezone }),
    [isEdit, formEvent, formik.values.startTime, orgTimezone],
  );
  const recurrenceStartDt = useMemo(
    () =>
      recurrenceStartDate
        ? DateTime.fromISO(recurrenceStartDate, orgTimezone ? { zone: orgTimezone } : {})
        : null,
    [recurrenceStartDate, orgTimezone],
  );
  // The backend caps a series at start + 2 years.
  const recurrenceEndMax = useMemo(
    () => (recurrenceStartDt ? recurrenceStartDt.plus({ years: 2 }) : undefined),
    [recurrenceStartDt],
  );

  const resourceManagerRef = useRef(null);

  const occurrenceCount = useMemo(() => {
    if (!isRecurring || !formik.values.recurrenceEndDate || !recurrenceStartDate) return 0;
    return countRecurrenceOccurrences({
      cadence: formik.values.cadence,
      startDate: recurrenceStartDate,
      endDate: formik.values.recurrenceEndDate,
    });
  }, [isRecurring, formik.values.cadence, formik.values.recurrenceEndDate, recurrenceStartDate]);

  const getEndRepeatLabel = () => {
    if (occurrenceCount <= 0) return createLang.END_REPEAT_LABEL;
    const countTemplate =
      occurrenceCount === 1 ? createLang.OCCURRENCES_COUNT_ONE : createLang.OCCURRENCES_COUNT;
    const countText = countTemplate.replace('{count}', formatNumber(occurrenceCount));
    return `${createLang.END_REPEAT_LABEL} (${countText})`;
  };
  const endRepeatLabel = <RequiredIndication label={getEndRepeatLabel()} />;

  const getModalTitle = () => {
    if (!isEdit) return createLang.CREATE_HEADER;
    if (!formEvent?.title) return createLang.EDIT_HEADER;
    return createLang.EDIT_HEADER_WITH_TITLE.replace('{title}', formEvent.title);
  };

  const hasResources = formik.values.knowledgeItems.length > 0;

  const usesWebLink = attendanceModeUsesWebLink(formik.values.format);
  const showPaste = usesWebLink && !formik.values.webLink;

  const pasteWebLink = async () => {
    if (!navigator.clipboard?.readText) return;
    try {
      const text = await navigator.clipboard.readText();
      formik.setFieldValue('webLink', text.trim());
    } catch {
      // The user declined the browser's clipboard prompt: the field is directly
      // editable, so a silent no-op is the intended fallback.
      return;
    }
  };

  return (
    <>
      <RecurringScopeModal
        open={!!scopeValues}
        title={createLang.SCOPE_EDIT_TITLE}
        message={createLang.SCOPE_EDIT_MESSAGE}
        lang={createLang}
        loading={patchMutation.isPending || updateGroup.isPending}
        onOnlyThis={handleScopeOnlyThis}
        onAllFuture={handleScopeAllFollowing}
        onCancel={() => setScopeValues(null)}
      />
      <Modal
        open={!!formMode}
        onCancel={closeForm}
        title={getModalTitle()}
        width={1110}
        className={styles.modal}
        closable={false}
        footer={
          <div className={classNames(styles.footer, { [styles.footerEnd]: isEdit })}>
            {!isEdit && (
              <Button type="default" onClick={() => resourceManagerRef.current?.openAddModal()}>
                {`+ ${createLang.ADD_RESOURCE_TITLE}`}
              </Button>
            )}
            <div className={styles.footerActions}>
              <Button type="default" onClick={closeForm} disabled={formik.isSubmitting}>
                {createLang.CANCEL}
              </Button>
              <Button
                type="primary"
                onClick={formik.handleSubmit}
                loading={formik.isSubmitting}
                disabled={hasErrors}>
                {isEdit ? createLang.SAVE_CHANGES : createLang.DONE}
              </Button>
            </div>
          </div>
        }>
        <form className={styles.grid} onSubmit={formik.handleSubmit}>
          <div className={styles.span2}>
            <TextInput
              name="title"
              label={<RequiredIndication label={createLang.EVENT_NAME_LABEL} />}
              placeholder={createLang.EVENT_NAME_PLACEHOLDER}
              value={formik.values.title}
              onChange={e => {
                formik.handleChange(e);
                formik.setFieldTouched('title', true, false);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.title && formik.errors.title}
              hideErrorText
            />
          </div>
          <LockTooltip locked={lockTimeAndHost} hint={lockHint} className={styles.span2}>
            <SelectBox
              name="hostIds"
              mode="multiple"
              label={<RequiredIndication label={createLang.HOST_LABEL} />}
              placeholder={createLang.HOST_PLACEHOLDER}
              options={hostOptions}
              value={formik.values.hostIds}
              onChange={(name, value) => formik.setFieldValue('hostIds', value)}
              error={formik.touched.hostIds && formik.errors.hostIds}
              hideErrorText
              disabled={lockTimeAndHost}
            />
          </LockTooltip>
          <TextInput
            name="speaker"
            label={createLang.SPEAKERS_LABEL}
            placeholder={createLang.SPEAKERS_PLACEHOLDER}
            value={formik.values.speaker}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <SelectBox
            name="format"
            label={lang.FORMAT}
            options={formatOptions}
            value={formik.values.format}
            onChange={(name, value) => formik.setFieldValue('format', value)}
          />
          <TextInput
            name="location"
            label={createLang.LOCATION_LABEL}
            placeholder={createLang.LOCATION_PLACEHOLDER}
            value={formik.values.location}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <TextInput
            name="webLink"
            label={createLang.VIRTUAL_LINK_LABEL}
            placeholder={createLang.VIRTUAL_LINK_PLACEHOLDER}
            disabled={!usesWebLink}
            value={usesWebLink ? formik.values.webLink : ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            // antd remounts the input when a suffix appears/disappears, dropping focus.
            suffix={
              showPaste ? (
                // Raw <button>: the design-system Button can't render inside an antd
                // input suffix without its own height and border.
                <button
                  type="button"
                  className={styles.pasteButton}
                  aria-label={createLang.PASTE_VIRTUAL_LINK}
                  onClick={pasteWebLink}>
                  {createLang.PASTE}
                </button>
              ) : (
                <span />
              )
            }
          />
          <LockTooltip locked={checkInOpensLocked} hint={lang.CHECK_IN_OPENS_LOCKED_HINT}>
            <SelectBox
              name="checkInOpensBeforeStartMinutes"
              label={lang.CHECK_IN_OPENS_LABEL}
              options={checkInOpensOptions}
              sortOptions={false}
              showSearch={false}
              value={formik.values.checkInOpensBeforeStartMinutes}
              onChange={(name, value) =>
                formik.setFieldValue('checkInOpensBeforeStartMinutes', value)
              }
              disabled={checkInOpensLocked}
            />
          </LockTooltip>
          <SelectBox
            name="checkInClosesAfterEndMinutes"
            label={lang.CHECK_IN_CLOSES_LABEL}
            options={checkInClosesOptions}
            sortOptions={false}
            showSearch={false}
            value={formik.values.checkInClosesAfterEndMinutes}
            onChange={(name, value) => formik.setFieldValue('checkInClosesAfterEndMinutes', value)}
          />
          <LockTooltip locked={lockTimeAndHost} hint={lockHint}>
            <DatePicker
              name="startTime"
              label={timeLabel(createLang.START_TIME_LABEL, formik.values.startTime)}
              showTime
              format={DATE_TIME_FORMAT}
              allowClear={false}
              timeZone={orgTimezone}
              disabled={lockTimeAndHost}
              getPopupContainer={trigger =>
                trigger?.closest('.ant-modal-container') || document.body
              }
              value={formik.values.startTime}
              onChange={value => {
                const nextStart = value ? value.toUTC().toISO() : '';
                formik.setFieldValue('startTime', nextStart);
                const { endTime, recurrenceEndDate } = formik.values;
                if (
                  nextStart &&
                  endTime &&
                  new Date(endTime).getTime() <= new Date(nextStart).getTime()
                ) {
                  formik.setFieldValue('endTime', '');
                }
                // Create only: clear the series end if the new start puts it out
                // of range. On edit the series start is fixed, so it can't.
                if (!isEdit && nextStart && recurrenceEndDate) {
                  const start = DateTime.fromISO(
                    nextStart,
                    orgTimezone ? { zone: orgTimezone } : {},
                  );
                  const minDate = start.toFormat('yyyy-LL-dd');
                  const maxDate = start.plus({ years: 2 }).toFormat('yyyy-LL-dd');
                  if (recurrenceEndDate < minDate || recurrenceEndDate > maxDate) {
                    formik.setFieldValue('recurrenceEndDate', '');
                  }
                }
              }}
            />
          </LockTooltip>
          <LockTooltip locked={lockTimeAndHost} hint={lockHint}>
            <DatePicker
              name="endTime"
              label={timeLabel(createLang.END_TIME_LABEL, formik.values.endTime)}
              showTime
              format={DATE_TIME_FORMAT}
              allowClear={false}
              timeZone={orgTimezone}
              disabled={lockTimeAndHost}
              minDate={endConstraints.minDate}
              disabledTime={endConstraints.disabledTime}
              getPopupContainer={trigger =>
                trigger?.closest('.ant-modal-container') || document.body
              }
              value={formik.values.endTime}
              onChange={value =>
                formik.setFieldValue('endTime', value ? value.toUTC().toISO() : '')
              }
              error={formik.touched.endTime && formik.errors.endTime}
            />
          </LockTooltip>
          <div className={styles.newRow}>
            <SelectBox
              name="allowSelfAttestation"
              label={lang.CHECK_IN_METHOD}
              options={checkInMethodOptions}
              sortOptions={false}
              showSearch={false}
              value={
                formik.values.allowSelfAttestation
                  ? CHECK_IN_METHOD_VALUES.SELF_ATTESTATION
                  : CHECK_IN_METHOD_VALUES.QR
              }
              onChange={(name, value) =>
                formik.setFieldValue(
                  'allowSelfAttestation',
                  value === CHECK_IN_METHOD_VALUES.SELF_ATTESTATION,
                )
              }
            />
          </div>
          <LockTooltip locked={lockTimeAndHost} hint={lockHint}>
            <SelectBox
              name="qrType"
              label={lang.QR_TYPE}
              options={qrTypeOptions}
              sortOptions={false}
              showSearch={false}
              disabled={lockTimeAndHost}
              value={formik.values.qrType}
              onChange={(name, value) => formik.setFieldValue('qrType', value)}
            />
          </LockTooltip>
          {showRepeatFields && (
            <SelectBox
              name="cadence"
              label={createLang.REPEAT_LABEL}
              options={cadenceOptions}
              value={formik.values.cadence}
              onChange={(name, value) => formik.setFieldValue('cadence', value)}
              sortOptions={false}
              showSearch={false}
            />
          )}
          {isRecurring && (
            <DatePicker
              name="recurrenceEndDate"
              label={endRepeatLabel}
              allowClear={false}
              timeZone={orgTimezone}
              minDate={recurrenceStartDt ? recurrenceStartDt.startOf('day') : undefined}
              maxDate={recurrenceEndMax}
              getPopupContainer={trigger =>
                trigger?.closest('.ant-modal-container') || document.body
              }
              value={formik.values.recurrenceEndDate || undefined}
              onChange={value =>
                formik.setFieldValue('recurrenceEndDate', value ? value.toFormat('yyyy-LL-dd') : '')
              }
            />
          )}
        </form>
        {!isEdit && (
          <>
            {hasResources && <Divider className={styles.divider} />}
            {/* Always mounted: the footer button opens the picker that lives inside. */}
            <div
              className={classNames(styles.resources, { [styles.resourcesHidden]: !hasResources })}>
              <span className={styles.resourcesLabel}>{createLang.ADDED_RESOURCES}</span>
              <ResourceManager
                ref={resourceManagerRef}
                hideAddButton
                closeOnAdd
                headerClassName={styles.resourcesHeader}
                listClassName={styles.resourcesCard}
                listWrapperClass={styles.resourceRow}
                renderItemOptions={RESOURCE_ROW_OPTIONS}
                resources={formik.values.knowledgeItems}
                canEdit
                modalTitle={createLang.ADD_RESOURCE_TITLE}
                contextHolder={resourceContextHolder}
                disabledResourceIds={(formik.values.knowledgeItems || [])
                  .map(item => item?.id)
                  .filter(Boolean)}
                onAddResource={payload => {
                  formik.setFieldValue('knowledgeItems', [
                    ...formik.values.knowledgeItems,
                    { ...payload, createdBy: userId },
                  ]);
                  resourceApi.success({
                    message: contentSelectorLang.RESOURCE_ADDED,
                    placement: 'bottomRight',
                  });
                }}
                onRemoveResource={itemToRemove => {
                  formik.setFieldValue(
                    'knowledgeItems',
                    formik.values.knowledgeItems.filter(
                      item => resourceKey(item) !== resourceKey(itemToRemove),
                    ),
                  );
                  resourceApi.success({
                    message: contentSelectorLang.RESOURCE_REMOVED,
                    placement: 'bottomRight',
                  });
                }}
                lang={createLang}
              />
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
